<template>
  <section class="content-group -bg-01 -center -vs-medium -column">
    <div class="group -narrow -flex -between -vs-center">
      <h1 class="heading -primary">
        <span v-show="entity && entity.title" class="heading -inline-sub">{{ entity.title }}</span>
        Shopping List
      </h1>

      <div v-if="from" class="spa-heading-actions">
        <div class="spa-heading-action">
          <a :href="from"> Back to {{ fromName }} </a>
        </div>
      </div>
    </div>

    <!-- week nav -->
    <div v-if="hasMultipleWeeks" class="group -narrow week-selector">
      <h2 class="heading -small demi">Select Week</h2>
      <ul
        v-for="(weekOptionChunk, chunkIndex) in weekOptionChunks"
        :key="chunkIndex"
        class="pill-btn-list"
        role="tablist"
        aria-label="Select an option to load the shopping list"
      >
        <li
          v-for="weekKey in weekOptionChunk"
          :id="weekKey"
          :key="weekKey"
          class="pill-btn"
          :class="{ '-active': weekKey === week }"
          role="tab"
          tabindex="0"
          :aria-label="`Shopping List for ${weekKey !== 'all' ? 'Week' : ''} ${
            weekOptions[weekKey]
          }`"
          :aria-selected="weekKey === week"
          aria-controls="shoppingList"
          @click="updateWeek(weekKey)"
          @keyup.enter="updateWeek(weekKey)"
        >
          <div class="">{{ weekOptions[weekKey] }}</div>
        </li>
      </ul>
    </div>

    <!-- shopping list -->
    <div
      id="shoppingList"
      ref="shoppingList"
      class="group -narrow -body-section"
      role="tabpanel"
      tabindex="0"
      :aria-labelledby="week"
    >
      <div
        v-for="(groceryTaxonomyId, index) in groceryDepartments"
        :key="groceryTaxonomyId"
        class="list-department"
      >
        <h2 class="heading -small demi">{{ groceryTaxonomyName(groceryTaxonomyId) }}</h2>
        <div class="group -vs-small">
          <ingredient-list
            :ingredients="selectedShoppingList.groceryDepartments[groceryTaxonomyId]"
            :measurement-system-selector="index === 0"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import _chunk from 'lodash/chunk'
import { mapState } from 'pinia'
import { useEntityContextStore } from '@/stores/entity-context'
import { scrollToElemTop } from '@/utils/scroll-to'
import IngredientList from '../content-sections/ingredient-list.vue'

/**
 * Powers the meal plan shopping lists.
 *
 * TODO: back button
 */
export default defineComponent({
  name: 'ShoppingList',
  components: { IngredientList },
  props: {
    // matches db schema
    groceryTaxonomy: {
      type: Array,
      required: true,
    },
    imperialTaxonomy: {
      type: Array,
      required: true,
    },
    metricTaxonomy: {
      type: Array,
      required: true,
    },

    // { full: '', 'week_1': '', ... }
    weekOptions: {
      type: Object,
      default: null,
    },

    // TODO: flow types
    // see ShoppingList.php service class for full output
    // { full: { groceryDepartments: {} }, 'week_1': { groceryDepartments: {} }, }
    shoppingLists: {
      type: Object,
      required: true,
    },

    initialWeek: {
      type: String,
      default: '',
    },
  },

  /* state */
  data() {
    // back link
    let from = ''
    let fromName = ''

    if (document && document.referrer) {
      const backTo = document.referrer

      if (backTo.includes('my/programs')) {
        from = new URL(document.referrer).pathname
        fromName = 'Program'
      }

      if (backTo.includes('my/calendar')) {
        from = new URL(document.referrer).pathname

        // add anchor to meal plan v2 section
        from += '#meal-plans'

        fromName = 'Calendar'
      }
    }
    // end back link

    // process initialWeek
    let week = 'week_1'

    if (this.initialWeek && this.shoppingLists[`week_${this.initialWeek}`]) {
      week = `week_${this.initialWeek}`
    }
    // end process initialWeek

    return {
      // default to first week
      week,

      // specific to meal plans
      // split the week options into chunks so we can display in separate lists
      weekOptionChunks: this.weekOptions ? this.chunkWeekOptions(this.weekOptions) : null,

      // add taxonomy to shopping lists
      shoppingListsWithTaxonomy: this.attachTaxonomyToIngredients(this.shoppingLists),

      // back link
      from,
      fromName,
    }
  },

  /* computed */
  computed: {
    // from Laravel detail view information: id, title, etc
    ...mapState(useEntityContextStore, ['entity']),

    // help to show / hide nav
    // all + week_1 = 2, if week_3 then it will be 2
    hasMultipleWeeks() {
      return !!(this.weekOptions && Object.keys(this.weekOptions).length > 2)
    },

    // shopping list for selected week
    selectedShoppingList() {
      if (!this.week || !this.shoppingListsWithTaxonomy[this.week]) {
        return {}
      }

      return this.shoppingListsWithTaxonomy[this.week]
    },

    // grocery taxonomy id array for the selected shopping week
    groceryDepartments() {
      if (!this.selectedShoppingList) {
        return []
      }

      // array format
      let groceryTaxonomyIds = Object.keys(this.selectedShoppingList.groceryDepartments)

      // force "0" to be last
      groceryTaxonomyIds.sort()

      if (groceryTaxonomyIds[0] === '0') {
        groceryTaxonomyIds = groceryTaxonomyIds.filter((id) => id !== '0')
        groceryTaxonomyIds.push('0')
      }

      return groceryTaxonomyIds
    },
  },

  /* watchers */
  watch: {
    // side effects when this.week changes, smooth scroll to ingredient list
    week(newVal: string, oldVal: string) {
      const refsShoppingList = this.$refs.shoppingList as HTMLElement

      if (newVal !== oldVal && refsShoppingList) {
        scrollToElemTop({
          elem: refsShoppingList,
          ms: 250,
        })
      }
    },
  },

  /* lifecycle */

  /* component methods */
  methods: {
    /**
     * Helper to chunk out a week list into two-dem array for multiple rows
     */
    chunkWeekOptions(weeks: object) {
      // save all options for last
      const weekKeys = Object.keys(weeks).filter((weekKey) => weekKey !== 'all')

      const chunks = _chunk(weekKeys, 4)

      // add all as the last option ONLY if more than one week
      if (weekKeys.length > 1) {
        const lastChunk = chunks[chunks.length - 1]
        lastChunk.push('all')
        chunks[chunks.length - 1] = lastChunk
      }

      return chunks
    },

    /**
     * Imperial and metric taxonomy are missing from ingredients
     * coming from backend (as they are stdClass and not Ingredient)
     * so add the relationships in eloquent-style
     */
    attachTaxonomyToIngredients(shoppingLists: object) {
      // hack to deep clone
      let shoppingListsWithTaxonomy = JSON.parse(JSON.stringify(shoppingLists))

      // avoid mutating props
      shoppingListsWithTaxonomy = Object.keys(shoppingLists).reduce((acc: any, weekKey: any) => {
        acc[weekKey] = shoppingListsWithTaxonomy[weekKey]

        // attach taxonomy to each grocery taxonomy ingredient list
        Object.keys(acc[weekKey].groceryDepartments).forEach((groceryTaxonomyId) => {
          const ingredients = acc[weekKey].groceryDepartments[groceryTaxonomyId].map(
            (ingredient: any) => {
              // attach taxonomy
              ingredient.imperial_taxonomy = this.imperialTaxonomy.find(
                (tax: any) => tax.id === ingredient.imperial_taxonomy_id,
              )
              ingredient.metric_taxonomy = this.metricTaxonomy.find(
                (tax: any) => tax.id === ingredient.metric_taxonomy_id,
              )

              // combine and comma separate notes
              ingredient.note = Array.isArray(ingredient.note) ? ingredient.note.join(', ') : ''

              return ingredient
            },
          )

          acc[weekKey].groceryDepartments[groceryTaxonomyId] = ingredients
        })

        return acc
      }, {})

      return shoppingListsWithTaxonomy
    },

    /**
     * setter
     */
    updateWeek(weekKey: string) {
      this.week = weekKey
    },

    groceryTaxonomyName(taxonomyId: string) {
      const taxonomy = this.groceryTaxonomy.find(
        (tax: any) => tax.id === Number.parseInt(taxonomyId),
      ) as any

      if (!taxonomy) {
        return 'Other'
      }

      return taxonomy.name
    },
  },
})
</script>
